import { IReportDefinition } from "@reports/models/IReportDefinition";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { IColumnHeader } from "../../../models/IColumnHeader";
import { DynamicReportService, IReportCategory } from "../../../_services/dynamicReports.service";
import "./newReportColumnSelector.component.scss";

export interface IReportRunCount {
    reportType: string;
    count: number;
    reportTypeTitle: string;
}

export interface IBindings {
    maxColumns: number;
    allColumns: IColumnHeader[];
    selectedColumns: IColumnHeader[];

    onColumnsChanged: (args: { columns: IColumnHeader[] }) => void;
}

class Controller implements angular.IComponentController, IBindings {

    // Bindings

    maxColumns: number;
    allColumns: IColumnHeader[] = [];
    selectedColumns: IColumnHeader[] = [];

    onColumnsChanged: (args: { columns: IColumnHeader[] }) => void;

    // Internal

    allCategories: IReportCategory[];
    reportCategories: IReportCategory[];
    popularReportCategories: IReportCategory[];

    searchString: string;

    loading = false;

    static $inject = ["$scope", "$q", "dynamicReportService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $q: angular.IQService,
        private readonly dynamicReportService: DynamicReportService) { }

    $onInit(): void {
        this.updateData();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
    }

    $onDestroy() {
    }

    onSearch() {

        var lowerCaseString = this.searchString && this.searchString.toLowerCase();

        // Reset all hide statuses
        this.allCategories.forEach(cat => {
            var columnShowCount = 0;

            cat.columns.forEach(col => {
                col.hide = this.searchString && col.Title.toLowerCase().indexOf(lowerCaseString) < 0;

                if (!col.hide) {
                    columnShowCount++;
                }
            });

            cat.hide = columnShowCount == 0;
            cat.showColumns = false;

            if (this.searchString && this.searchString.length > 1 && columnShowCount < cat.columns.length) {
                cat.showColumns = true;
            }
        });

        // If we only have one category, auto open it up
        if (this.allCategories.length == 1) {
            this.allCategories[0].showColumns = true;
        }

        this.$scope.$applyAsync();
    }

    onCategoryClicked(cat: IReportCategory) {
        cat.showColumns = !cat.showColumns;
    }

    onColumnClicked(column: IColumnHeader) {
        const columnCopy = copy(column);

        if (!this.selectedColumns) {
            this.selectedColumns = [];
        }

        // If we have this column in already, remove it, else add it
        const matchingColumns = this.selectedColumns.filter(c => c.DefaultTitle == column.DefaultTitle && c.ReportType == column.ReportType);
        if (matchingColumns.length != 0) {
            // Always remove the latest, if there are more than one
            this.selectedColumns = this.selectedColumns.filter(c => c != matchingColumns[matchingColumns.length - 1]);
        }
        else {
            this.selectedColumns.push(columnCopy);
        }

        if (this.maxColumns && this.selectedColumns.length > this.maxColumns) {
            this.selectedColumns.shift();
        }

        this.updateSelectedColumnState();

        if (this.onColumnsChanged) {
            this.onColumnsChanged({ columns: this.selectedColumns })
        };
    }

    // ---

    private updateData() {
        const reportDefinitionPromise = this.dynamicReportService.getReports();
        const historyPromise = this.dynamicReportService.getLatestReportHistory();
        const columnHeaderPromise = this.dynamicReportService.getDisplayColumnHeaders();

        this.loading = true;

        this.$q
            .all([reportDefinitionPromise, historyPromise, columnHeaderPromise])
            .then(([reports, history, headers]) => {
                if (!this.allColumns || this.allColumns.length == 0) {
                    this.allColumns = headers;
                }

                //console.log(this.allColumns, headers);

                //headers.forEach(h => {
                //    if (h.Title.indexOf ("ounds")>=0) {

                //        console.log(1, h, h.ColumnDefinition.Format.IsVisible, h.dataSourceDefinition.IsVisible);
                //    }
                //});

                this.allColumns = this.allColumns.filter(h => h.ColumnDefinition.Format.IsVisible && h.dataSourceDefinition.IsVisible);

                //console.log("headers",headers);
                //this.allColumns.forEach(h => {
                //    if (h.Title == "Journey Start Time") {

                //        console.log(2, h);
                //    }
                //});

                this.allCategories = this.dynamicReportService.getUniqueReportCategories(this.allColumns);

                // Filter out reports that havent been run recently
                const recentlyRunReports = reports.filter(r => history.find(h => h.ReportDefinitionId == r.Id));

                var popularReportRunCount = this.createReportCount(recentlyRunReports);
                this.popularReportCategories = this.getReportCategoriesFromReportCounts(popularReportRunCount, reports);

                // Never show General in the Popular categories
                this.popularReportCategories = this.popularReportCategories.filter(c => c.title != "General");

                // Remove any popular columns from the "All category"
                this.reportCategories = this.allCategories.filter(cat => this.popularReportCategories.indexOf(cat) < 0);

                this.updateSelectedColumnState();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createReportCount(reports: IReportDefinition[]): IReportRunCount[] {
        let popularReports: IReportRunCount[] = [];

        reports.forEach(report => {
            report.TableDefinition.Headers.forEach(header => {
                var popularReportEntry = popularReports.find(p => p.reportType == header.ReportType);

                if (popularReportEntry) {
                    popularReportEntry.count++;
                }
                else {
                    popularReports.push({ reportType: header.ReportType, count: 1, reportTypeTitle: "Unknown??" });
                }
            });
        });

        // Now sort based on popularity
        popularReports.sort((p1, p2) => { return p2.count - p1.count; });
        return popularReports;
    }

    private getReportCategoriesFromReportCounts(reportRunCount: IReportRunCount[], reports: IReportDefinition[]): IReportCategory[] {
        const categories: IReportCategory[] = [];

        reportRunCount.forEach(runCount => {
            const matchingReport = reports.find(r => r.reportTypes.indexOf(runCount.reportType) >= 0);

            if (matchingReport) {
                const reportCategories = this.dynamicReportService.getUniqueReportCategories(matchingReport.TableDefinition.Headers);

                reportCategories.forEach(reportCategory => {
                    // Check to ensure we havent already added this type
                    if (!categories.find(c => c.title == reportCategory.title)) {
                        const matchedCategory = this.allCategories.find(rc => rc.title == reportCategory.title);

                        if (matchedCategory) {
                            categories.push(matchedCategory);
                        }
                    }
                });
            }
        });

        // If we only have one category, auto open it up
        if (categories.length == 1) {
            categories[0].showColumns = true;
        }

        return categories;
    }

    private updateSelectedColumnState() {
        if (!this.allCategories || !this.selectedColumns) {
            return;
        }

        //console.log("updateSelectedColumnState", this.allCategories);

        this.allCategories.forEach(cat => {

            cat.columns.forEach(col => {
                col.selectedCount = this.selectedColumns.filter(c => c.Title == col.Title).length
                col.selected = col.selectedCount != 0;

                //if (col.selected) {
                //    console.log(col);
                //}
            })
        });
    }
}

export class NewReportColumnSelectorComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            allColumns: "<",
            selectedColumns: "<",
            maxColumns: "<",

            onColumnsChanged: "&"
        };

        this.controller = Controller;
        this.template = require("./newReportColumnSelector.component.html");
    }
}
